import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Button } from "@mui/material";
import Iconify from "@/components/iconify/iconify";
import ResetClimberProfileModal from "@/components/modals/ResetClimberProfileModal";
import { useBoolean } from "@/hooks/use-boolean";
import { useResetCurrentUser } from "@/hooks/useResetCurrentUser";
import { EDUCATOR_EVENT_TYPES, useReporting } from "@/hooks/useReporting";

const FloatingToolbarContainer = styled("div", {
  label: "FloatingToolbarContainer",
})({
  position: "fixed",
  bottom: 0,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1200,
  display: "flex",
  alignItems: "center",

  padding: 0,
  borderRadius: 20,
});

// Reset is destructive and used rarely, so it sits away from Exit rather than
// beside it — adjacent hit areas made it easy to wipe a climber profile while
// reaching for Exit.
const ResetContainer = styled("div", {
  label: "ResetContainer",
})({
  position: "fixed",
  bottom: 0,
  right: 24,
  zIndex: 1200,
  display: "flex",
  alignItems: "center",
});

const buttonStyles = {
  borderRadius: 16,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  mb: 2,
} as const;

export const TeacherFloatingToolbar = () => {
  const user = useCurrentUser();
  const confirmReset = useBoolean(false);
  const { resetUser, isLoading, error, clearError } = useResetCurrentUser();
  const { reportClimberReset } = useReporting();

  if (user?.id.startsWith("cli-")) {
    return null;
  }

  const dashboardlink = `/${user.role}/dashboard`;

  const handleCloseReset = () => {
    // Drop any previous failure, or reopening the modal shows an error the coach
    // hasn't earned yet.
    clearError();
    confirmReset.onFalse();
  };

  const handleConfirmReset = async () => {
    try {
      await reportClimberReset();
      // A full reset, so the climber-mode event- records go and badges and next
      // steps — both re-derived from event history on every read — rebuild from
      // empty. The coach's own events are stamped with the same id, so they are
      // named here to survive; without that a coach would lose their login and
      // resource-download history every time they tidied up Climber Mode.
      await resetUser({
        confirmation: "reset me",
        options: { full: true },
        preserveEventTypes: EDUCATOR_EVENT_TYPES,
      });
      // Hard navigation so every cached query and store is rebuilt from the
      // now-empty data, landing the coach back on their own dashboard.
      window.location.href = dashboardlink;
    } catch {
      // useResetCurrentUser logs the error; the modal surfaces it.
    }
  };

  return (
    <>
      <FloatingToolbarContainer>
        <Button
          component={Link}
          color="secondary"
          to={dashboardlink}
          variant="contained"
          size="small"
          startIcon={<Iconify icon="eva:arrow-back-fill" width={16} />}
          sx={buttonStyles}
        >
          Exit Climber Mode
        </Button>
      </FloatingToolbarContainer>
      <ResetContainer>
        <Button
          color="error"
          variant="contained"
          size="small"
          onClick={confirmReset.onTrue}
          startIcon={<Iconify icon="eva:refresh-fill" width={16} />}
          sx={buttonStyles}
        >
          Reset
        </Button>
      </ResetContainer>
      <ResetClimberProfileModal
        open={confirmReset.value}
        onClose={handleCloseReset}
        onConfirm={handleConfirmReset}
        isLoading={isLoading}
        error={error}
      />
    </>
  );
};
